/* oxlint-disable sorare/no-detached-fragments */
import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useApolloClient, useMutation } from '@apollo/client/react';
import gqlMacro from 'graphql-tag.macro';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useInterval, useTimeout } from 'react-use';

import type { FiatCurrency, Sport } from '#src/__generated__/globalTypes.ts';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { MobileSeriesUserCurrencies } from '#src/components/navigation/AppNavigation/UserCurrencies/MobileSeriesUserCurrencies/index.tsx';
import { UserNav } from '#src/components/navigation/UserNav/index.tsx';
import { RedirectAfterSignIn } from '#src/contexts/currentUser/RedirectAfterSignIn.tsx';
import { currentUser } from '#src/contexts/currentUser/queries.ts';
import { coreCache } from '#src/contexts/graphql/Provider.tsx';
import { useGraphqlContext } from '#src/contexts/graphql/index.tsx';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useTMContext } from '#src/contexts/tm/index.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useQueryLoader } from '#src/hooks/graphql/useQueryLoader.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { usePageVisibility } from '#src/hooks/usePageVisibility.tsx';
import { useRefreshOnPageVisible } from '#src/hooks/useRefreshOnPageVisible.ts';
import {
  getMinorRate,
  monetaryAmountFragment,
  ratesFragment,
} from '#src/lib/monetaryAmount.ts';
import { queryLoader } from '#src/lib/navigation/queryLoader.ts';
import {
  importantNotificationCategories,
  notificationCategories,
} from '#src/lib/notifications.ts';
import { useCardSetBranding } from '#src/set/branding/useCardSetBranding.ts';

import type {
  ConfigProvider_exchangeRate,
  ConfigProvider_TeamsPlayingNextGameWeek,
  ConfigQueryVariables,
  ExchangeRateQuery,
  ExchangeRateQueryVariables,
  PingConfigQuery,
  PingConfigQueryVariables,
  ReportTelemetry,
  ReportTelemetryVariables,
  ScoutableSo5Competitions_query,
  UpcomingSo5Fixture_query,
} from './__generated__/Provider.graphql.ts';
import type { AlgoliaCardIndexes, AlgoliaIndexes } from './index.ts';
import { ConfigContext } from './index.ts';
import type {
  ConfigQuery,
  ConfigQuery_currentUser,
  ConfigQuery_exchangeRate,
} from './types.ts';

const teamPlayingNextGameWeekFragment = gqlMacro`
  fragment ConfigProvider_TeamsPlayingNextGameWeek on TeamsPlayingNextGameWeek {
    slug
    gameWeek
    shortDisplayName
    longDisplayName
    endDate
    startDate
    teamSlugs
  }
` as TypedDocumentNode<ConfigProvider_TeamsPlayingNextGameWeek>;

const configExchangeRateFragment = gqlMacro`
  fragment ConfigProvider_exchangeRate on ExchangeRate {
    id
    ethRates {
      ...RatesFragment_rates
    }
    time
  }
  ${ratesFragment}
` as TypedDocumentNode<ConfigProvider_exchangeRate>;

const scoutableSo5CompetitionsFragment = gql`
  fragment ScoutableSo5Competitions_query on Query {
    so5 {
      id
      currentManagerHasLiveGames(sport: $sport)
      cardMaxGrade(sport: $sport)
      inSeasonName: seasonalityName(seasonality: IN_SEASON, sport: $sport)
      allSeasonsName: seasonalityName(seasonality: ALL_SEASONS, sport: $sport)
      myAvailableFixtureStory {
        id
        aasmState
      }
      so5Competitions(sport: $sport, eventType: CLASSIC) {
        slug
        displayName
        logoUrl
        iconUrl
        backgroundUrl
        algoliaFilters
        scoutable
        competitions {
          slug
          displayName
          pictureUrl
        }
      }
    }
  }
` as TypedDocumentNode<ScoutableSo5Competitions_query>;

const upcomingSo5FixtureFragment = gql`
  fragment UpcomingSo5Fixture_query on Query {
    so5 {
      nextSo5Fixture: so5Fixture(type: UPCOMING, sport: $sport) {
        slug
        cutOffDate
      }
    }
  }
` as TypedDocumentNode<UpcomingSo5Fixture_query>;

const CONFIG_QUERY = gqlMacro`
  query ConfigQuery(
    $sport: Sport!
    $notificationCategories: [NotificationCategoryInput!]!
    $importantNotificationCategories: [NotificationCategoryInput!]!
    $withCollectionPredictions: Boolean = true
  ) {
    config {
      id
      algoliaIndexSuffix
      algoliaApplicationId
      algoliaSearchApiKey
      baseBankAddress
      ethereumBankDepositAddress
      baseCrossDomainDepositAddress
      l1MessengerAddress
      starkExchangeAddress
      ethereumGatewayMinterProxyAddress
      ethereumNetworkId
      ethereumEndpoint
      solanaBankAuthority
      sorareEncryptionKey
      minimumReceiveWeiAmount
      marketFeeRateBasisPoints
      idealDepositFeesAmountMinor
      payByBankDepositFeesAmountMinor
      # eslint-disable-next-line @graphql-eslint/no-deprecated -- fiat-deposit credit-card fee (AddFundsToFiatWallet); PAY-347 fee matrices cover market checkout only, no deposit-specific replacement exists yet (PAY-372)
      mangopayAmexFeesBasisPoints: creditCardFeesBasisPoints(
        creditCardType: AMEX
        provider: MANGOPAY
      )
      # eslint-disable-next-line @graphql-eslint/no-deprecated -- fiat-deposit credit-card fee (AddFundsToFiatWallet); PAY-347 fee matrices cover market checkout only, no deposit-specific replacement exists yet (PAY-372)
      mangopayVisaFeesBasisPoints: creditCardFeesBasisPoints(
        creditCardType: CB_VISA_MASTERCARD
        provider: MANGOPAY
      )
      paypalDepositFeesBasisPoints
      primaryMarketPaymentFeeRates {
        paymentMethod
        creditCardType
        currency
        feeBasisPoints
      }
      secondaryMarketPaymentFeeRates {
        paymentMethod
        creditCardType
        currency
        feeBasisPoints
      }
      mangopayApplePayDepositFeesBasisPoints
      paypalMinimumPrimaryMarketAmount: minimumPrimaryMarketAmount(
        paymentMethod: PAYPAL
      ) {
        ...MonetaryAmountFragment_monetaryAmount
      }
      paypalMinimumSecondaryMarketAmount: minimumSecondaryMarketAmount(
        paymentMethod: PAYPAL
      ) {
        ...MonetaryAmountFragment_monetaryAmount
      }
      exchangeRate {
        ...ConfigProvider_exchangeRate
      }
      defaultFiatCurrency
      ethQuantum
      so5 {
        id
        teamsPlayingNextDailyGameWeeks: teamsPlayingNextGameWeeks(
          eventType: DAILY
          sport: $sport
        ) {
          slug
          ...ConfigProvider_TeamsPlayingNextGameWeek
        }
        teamsPlayingNextClassicGameWeeks: teamsPlayingNextGameWeeks(
          eventType: CLASSIC
          sport: $sport
          first: 5
        ) {
          slug
          ...ConfigProvider_TeamsPlayingNextGameWeek
        }
        dailyTasksExpiresAt: tasksExpiresAt(periodicity: DAILY)
      }
      currentLocation {
        countryCode
        regionCode
      }
      counts {
        usersCount
      }
      activeRevealableCardCampaigns(sport: $sport) {
        slug
        name
        environmentPictureUrl
        blueprintCardEditions
        season {
          startYear
        }
      }
      referralProgramConfig(sport: $sport) {
        id
        maxDiscount {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
    }
    currentUser {
      slug
      ...CurrentUserProvider_currentUser
    }
    tokens {
      promotionalEvents: promotionalEvents(sport: $sport) {
        slug
        name
        title
        blogUrl
        description
        logoUrl
        pictureUrl
        backgroundPictureUrl
        mobileBackgroundPictureUrl
        filterPictureUrl
        secondaryBannerPictureUrl
        startDate
        endDate
        algoliaFilters
        heroValueProps {
          iconUrl
          text
        }
        ... on MarketPromotionalEvent {
          slug
          hasInstantBuy
        }
        ... on SpecialEditionPromotionalEvent {
          slug
          hasInstantBuy
        }
        ... on CardShardThresholdsPromotionalEvent {
          slug
          eligibilityRules {
            id
            eligibleRarities
          }
        }
      }
    }
    market {
      id
      myCommonDailyClaimTask(sport: $sport) {
        id
        aasmState
      }
      commonWheelTasks: myWheelTasks(sport: $sport, rarity: common) {
        id
        aasmState
      }
      limitedWheelTasks: myWheelTasks(sport: $sport, rarity: limited) {
        id
        aasmState
      }
      rareWheelTasks: myWheelTasks(sport: $sport, rarity: rare) {
        id
        aasmState
      }
      superRareWheelTasks: myWheelTasks(sport: $sport, rarity: super_rare) {
        id
        aasmState
      }
      uniqueWheelTasks: myWheelTasks(sport: $sport, rarity: unique) {
        id
        aasmState
      }
      commonWheelConfig: activeWheelConfig(rarity: common, sport: $sport) {
        slug
        ticketDistributionEndDate
      }
      limitedWheelConfig: activeWheelConfig(rarity: limited, sport: $sport) {
        slug
        ticketDistributionEndDate
      }
      rareWheelConfig: activeWheelConfig(rarity: rare, sport: $sport) {
        slug
        ticketDistributionEndDate
      }
      superRareWheelConfig: activeWheelConfig(rarity: super_rare, sport: $sport) {
        slug
        ticketDistributionEndDate
      }
      uniqueWheelConfig: activeWheelConfig(rarity: unique, sport: $sport) {
        slug
        ticketDistributionEndDate
      }
    }
    cardSet {
      id
      currentSet(sport: $sport) {
        slug
        name
        endDate
        ...MobileSeriesUserCurrencies_cardSet
        ...UserNav_cardSet
        ...useCardSetBranding_cardSet
      }
      nextSet(sport: $sport) {
        slug
        name
        startDate
        endDate
        ...MobileSeriesUserCurrencies_cardSet
        ...UserNav_cardSet
        ...useCardSetBranding_cardSet
      }
      maximumInviteTasksAsReferrer(sport: $sport)
      replayFinished(sport: $sport)
    }
    ...ScoutableSo5Competitions_query
    ...UpcomingSo5Fixture_query
  }
  ${currentUser}
  ${teamPlayingNextGameWeekFragment}
  ${scoutableSo5CompetitionsFragment}
  ${upcomingSo5FixtureFragment}
  ${configExchangeRateFragment}
  ${monetaryAmountFragment}
  ${MobileSeriesUserCurrencies.fragments.cardSet}
  ${UserNav.fragments.cardSet}
  ${useCardSetBranding.fragments.cardSet}
` as TypedDocumentNode<ConfigQuery, ConfigQueryVariables>;

const PING_QUERY = gqlMacro`
  query PingConfigQuery($sport: Sport!) {
    config {
      id
      exchangeRate {
        ...ConfigProvider_exchangeRate
      }
      minimumReceiveWeiAmount
    }
    so5 {
      currentManagerHasLiveGames(sport: $sport)
    }
    currentUser {
      slug
    }
  }
  ${configExchangeRateFragment}
` as TypedDocumentNode<PingConfigQuery, PingConfigQueryVariables>;

/**
 * The backend only accepts the exchange rate id of the generation it currently
 * considers live, so a settlement built on a stale id is rejected outright with
 * `exchange rate has expired`. This lets callers pull the current rate on
 * demand and read it back synchronously, rather than waiting for the ping poll
 * below to happen to land first.
 */
const EXCHANGE_RATE_QUERY = gqlMacro`
  query ExchangeRateQuery {
    config {
      id
      exchangeRate {
        ...ConfigProvider_exchangeRate
      }
    }
  }
  ${configExchangeRateFragment}
` as TypedDocumentNode<ExchangeRateQuery, ExchangeRateQueryVariables>;

const TM_MUTATION = gqlMacro`
  mutation ReportTelemetry($input: reportTelemetryInput!) {
    reportTelemetry(input: $input) {
      errors {
        message
      }
    }
  }
` as TypedDocumentNode<ReportTelemetry, ReportTelemetryVariables>;

type Props = {
  children: ReactNode;
};

const getRandomInt = (max: number) => {
  return Math.floor(Math.random() * max);
};

const pingInterval = 115_000 + getRandomInt(10_000); // 2Min +-5 sec;
const backgroundPingInterval = 1_795_000 + getRandomInt(10_000); //30 Min +- 5sec;
const tmInterval = 30_000 + getRandomInt(5_000);

const configQueryVariables = (sport: Sport) => ({
  sport,
  notificationCategories,
  importantNotificationCategories,
});

export const loader = queryLoader(CONFIG_QUERY, ({ sport }) =>
  configQueryVariables(sport)
);

/**
 * Provides environment dependent configuration keys.
 * Aggregates all startup loading states so that initialization can be done in parallel
 */
export const ConfigProvider = ({ children }: Props) => {
  const { data, refetch, loading } = useQueryLoader<ConfigQuery>();
  const { sport } = useSportContext();
  const [isPingQueryReady] = useTimeout(pingInterval);

  const isVisible = usePageVisibility();

  useQuery(PING_QUERY, {
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
    pollInterval: isVisible ? pingInterval : backgroundPingInterval,
    skip: !isPingQueryReady(),
    variables: { sport },
  });

  const client = useApolloClient();

  /**
   * Fetches the live exchange rate and returns it. Writing through the cache
   * also updates every `useConfigContext().exchangeRate` consumer, but callers
   * mid-flight in an async callback must use the returned value: their own
   * render has already closed over the previous one.
   *
   * Concurrent calls share one request, so opening a payment sheet while a
   * tab-return refresh is still in flight does not fetch the rate twice.
   */
  const inFlightRefresh = useRef<Promise<ConfigQuery_exchangeRate>>(undefined);

  const refreshExchangeRate = useCallback(async () => {
    if (inFlightRefresh.current) return inFlightRefresh.current;

    const request = client
      .query({ query: EXCHANGE_RATE_QUERY, fetchPolicy: 'network-only' })
      .then(({ data: refreshed }) => {
        const rate = refreshed?.config.exchangeRate;
        if (!rate) throw new Error('Could not refresh the exchange rate');
        return rate;
      })
      .finally(() => {
        inFlightRefresh.current = undefined;
      });

    inFlightRefresh.current = request;
    return request;
  }, [client]);

  /**
   * The refresh already running, if any. Settlement paths await it rather than
   * racing it: a rate pulled on tab return lands a moment after the tab is
   * usable again, and a user who clicks straight through would otherwise send
   * the id the refresh is in the middle of replacing.
   */
  const pendingExchangeRateRefresh = useCallback(
    (): Promise<ConfigQuery_exchangeRate> | undefined =>
      inFlightRefresh.current,
    []
  );

  // Flipping pollInterval back on when the tab returns restarts the interval
  // without fetching, so the rate is pulled explicitly on the way back in.
  useRefreshOnPageVisible(refreshExchangeRate);
  const {
    flags: { useReportTelemetry },
  } = useFeatureFlags();

  const { refreshWsCable } = useGraphqlContext();
  const flushOperations = useTMContext()?.flushOperations;
  const [tm] = useMutation(TM_MUTATION, {
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
  });

  const algoliaIndexSuffix = data?.config.algoliaIndexSuffix;

  const { algoliaCardIndexes, algoliaIndexes } = useMemo(() => {
    const aci: AlgoliaCardIndexes = {
      // blockchain cards indices
      New: `BlockchainCard_New${algoliaIndexSuffix}`,
      'Ending Soon': `BlockchainCard_EndingSoon${algoliaIndexSuffix}`,
      'Newly Listed': `BlockchainCard_NewlyListed${algoliaIndexSuffix}`,
      'Highest Average Score': `BlockchainCard_HighestAverageScore${algoliaIndexSuffix}`,
      'Highest Price': `BlockchainCard${algoliaIndexSuffix}`,
      'Lowest Price': `BlockchainCard_LowestPrice${algoliaIndexSuffix}`,
      'Best Value': `BlockchainCard_BestValue${algoliaIndexSuffix}`,
      'Popular Player': `BlockchainCard_Popularity${algoliaIndexSuffix}`,

      'Cards On Sale Ending Soon': `CardsOnSale_EndingSoon${algoliaIndexSuffix}`,
      'Cards On Sale Newly Listed': `CardsOnSale_NewlyListed${algoliaIndexSuffix}`,
      'Cards On Sale Highest Average Score': `CardsOnSale_HighestAverageScore${algoliaIndexSuffix}`,
      'Cards On Sale Highest Price': `CardsOnSale${algoliaIndexSuffix}`,
      'Cards On Sale Lowest Price': `CardsOnSale_LowestPrice${algoliaIndexSuffix}`,
      'Cards On Sale Best Value': `CardsOnSale_BestValue${algoliaIndexSuffix}`,
      'Cards On Sale Popular Player': `CardsOnSale_Popularity${algoliaIndexSuffix}`,
      'Cards On Sale Bids Count': `CardsOnSale_BidsCount${algoliaIndexSuffix}`,
      'Cards On Sale Starting Prediction': `CardsOnSale_StarterOdds${algoliaIndexSuffix}`,
    };

    const ai: AlgoliaIndexes = {
      ...aci,
      User: `User${algoliaIndexSuffix}`,
      Player: `Player${algoliaIndexSuffix}`,
      Club: `Club${algoliaIndexSuffix}`,
      League: `League${algoliaIndexSuffix}`,
      Country: `Country${algoliaIndexSuffix}`,
      'National Team': `NationalTeam${algoliaIndexSuffix}`,
      Competition: `Competition${algoliaIndexSuffix}`,
    };
    return {
      algoliaIndexes: ai,
      algoliaCardIndexes: aci,
    };
  }, [algoliaIndexSuffix]);

  const value = useMemo(() => {
    if (!data) return null;
    const { config, tokens, so5, market, cardSet } = data;
    const { exchangeRate, minimumReceiveWeiAmount } = config;

    return {
      ...config,
      ...tokens,
      ...market,
      cardSet,
      so5: {
        ...config.so5,
        ...so5,
      },
      wheel: {
        tasks: [
          ...market.commonWheelTasks,
          ...market.limitedWheelTasks,
          ...market.rareWheelTasks,
          ...market.superRareWheelTasks,
          ...market.uniqueWheelTasks,
        ],
        commonWheelTasks: market.commonWheelTasks,
        commonWheelConfig: market.commonWheelConfig,
        limitedWheelConfig: market.limitedWheelConfig,
        rareWheelConfig: market.rareWheelConfig,
        superRareWheelConfig: market.superRareWheelConfig,
        uniqueWheelConfig: market.uniqueWheelConfig,
      },
      marketFeeRateBasisPoints: config.marketFeeRateBasisPoints / 10000,
      marketFeePercentagePoints: BigInt(config.marketFeeRateBasisPoints) / 100n,
      minimumReceiveWeiAmount,
      algoliaIndexes,
      algoliaCardIndexes,
      currentUser: data.currentUser,
      exchangeRate: {
        ...exchangeRate,
        getEthRate: (to: FiatCurrency) =>
          getMinorRate(exchangeRate.ethRates, to).div(100).toNumber(),
      },
      refetch,
      refreshExchangeRate,
      pendingExchangeRateRefresh,
      loading,
      updateQuery: (newCurrentUser: ConfigQuery_currentUser) => {
        coreCache.updateQuery(
          {
            query: CONFIG_QUERY,
            variables: configQueryVariables(sport),
          },
          existing => existing && { ...existing, currentUser: newCurrentUser }
        );
        refreshWsCable();
      },
      defaultFiatCurrency: config.defaultFiatCurrency,
    };
  }, [
    data,
    refetch,
    refreshExchangeRate,
    pendingExchangeRateRefresh,
    loading,
    refreshWsCable,
    algoliaCardIndexes,
    algoliaIndexes,
    sport,
  ]);

  useInterval(
    () => {
      if (flushOperations) {
        const operations = flushOperations();
        if (!operations.length) {
          return;
        }
        tm({ variables: { input: { operations } } });
      }
    },
    useReportTelemetry ? tmInterval : null
  );

  if (!value) return <LoadingIndicator fullScreen />;

  return (
    <RedirectAfterSignIn currentUser={value.currentUser}>
      <ConfigContext value={value}>{children}</ConfigContext>
    </RedirectAfterSignIn>
  );
};
